import { useCallback, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import MdiLoading from '~icons/mdi/loading'
import { cx } from 'src/utils/cx'
import { getMediaUrl } from 'src/utils/media'
import { downloadFile } from '../ArtworkDetail/utils'
import { CircularSpinner } from '../GenerationTask/Common/CircularSpinner'
import type { SingleImageViewerProps } from './SingleImageViewer'
import SingleImageViewer from './SingleImageViewer'
import { VideoMedia } from './VideoMedia'

type SingleMediaViewerProps = {
  mediaType: 'image' | 'video'
  preferVideo?: boolean
  videoHasAudio?: boolean
} & SingleImageViewerProps

export default function SingleMediaViewer(props: SingleMediaViewerProps) {
  const { preferVideo, videoHasAudio, videoMediaId, ...rest } = props

  if (!videoMediaId || !preferVideo) return <SingleImageViewer {...rest} />

  return (
    <VideoMediaWrapper
      {...rest}
      videoMediaId={videoMediaId}
      videoHasAudio={videoHasAudio}
    />
  )
}

function VideoMediaWrapper(
  props: SingleMediaViewerProps & { videoMediaId: string },
) {
  const [full, setFull] = useState(false)
  const [hasVideoLoaded, setHasVideoLoaded] = useState(false)
  const videoUrl = getMediaUrl(props.videoMediaId, 'orig')
  const videoRef = useRef<HTMLVideoElement>(null)

  const onVideoLoad = useCallback(() => {
    setHasVideoLoaded(true)
  }, [])

  if (!videoUrl) return <CircularSpinner />

  return (
    <>
      <VideoMedia
        ref={videoRef}
        src={videoUrl}
        poster={props.thumbnailUrl}
        autoPlay={true}
        muted={!props.videoHasAudio}
        loop={true}
        className={cx('object-top object-contain', props.imgClassName)}
        onClick={() => {
          setFull(true)
          videoRef.current?.pause()
        }}
        onLoadedData={onVideoLoad}
      />
      {/* Video loading overlay */}
      {!hasVideoLoaded && (
        <div className="absolute inset-0 bg-white/40 dark:bg-neutral-900/60 z-20">
          <MdiLoading className="size-12 absolute inset-0 m-auto animate-spin" />
        </div>
      )}
      {full && (
        <FullWindowVideo
          videoUrl={videoUrl}
          fileName={`pixai-${props.videoMediaId}.mp4`}
          videoHasAudio={props.videoHasAudio}
          onClose={() => {
            videoRef.current?.play()
            setFull(false)
          }}
        />
      )}
    </>
  )
}

/**
 * Not fullscreen but use portal to simulate a lightbox effect
 * @param props
 * @returns
 */
export function FullWindowVideo(props: {
  videoUrl: string
  fileName: string
  videoHasAudio?: boolean
  onClose?: () => void
}) {
  function download() {
    downloadFile(props.videoUrl, props.fileName, { skipConversion: true })
  }

  return createPortal(
    <div
      className="left-0 top-0 fixed w-full h-full z-[100000] grid place-content-center bg-black/80"
      tabIndex={-1}
      role="dialog"
      onClick={() => props.onClose?.()}
    >
      <VideoMedia
        src={props.videoUrl}
        // NOTE: max-h-full doesn't work, though container's height is 100vh which is same as h-screen
        className="max-w-full max-h-screen object-contain"
        autoPlay
        muted={!props.videoHasAudio}
        controls
      />
      <div className="p-3 absolute top-0 right-0 flex items-center gap-4 text-white">
        <button
          onClick={ev => {
            ev.stopPropagation()
            download()
          }}
        >
          <svg aria-hidden="true" viewBox="0 0 32 32" width="32" height="32">
            <path
              d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <button onClick={() => props.onClose?.()}>
          <svg aria-hidden="true" viewBox="0 0 32 32" width="32" height="32">
            <path
              d="M24 10l-2-2-6 6-6-6-2 2 6 6-6 6 2 2 6-6 6 6 2-2-6-6z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>
    </div>,
    document.body,
  )
}
